import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiError } from '../api/client';
import { useSession, type CurrentUser } from '../api/session';
import { Button, Field, TextInput, ErrorNote } from '../components/ui';
import { DEVELOPER_CREDIT, DEVELOPER_URL } from '../components/credit';

export function Login() {
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [revealed, setRevealed] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);
    const [busy, setBusy] = useState(false);

    const navigate = useNavigate();
    const { branding, refresh } = useSession();

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError(null);

        try {
            const user = await api.post<CurrentUser>('/api/auth/login', { userName, password });
            await refresh();

            // A forced change is not a suggestion: the seeded administrator arrives here and must
            // not be able to navigate away with the generated password still active.
            navigate(user.mustChangePassword ? '/change-password' : '/', { replace: true });
        } catch (caught) {
            setError(caught as ApiError);
        } finally {
            setBusy(false);
        }
    };

    const productName = branding?.productName ?? 'AI Interview';
    const hasLogo = !!(branding?.logoPath || branding?.secondaryLogoPath);

    return (
        <div className="auth-wrap">
            <aside className="auth-aside">
                <div>
                    {hasLogo
                        ? (
                            <span className="brand-logos">
                                {branding!.logoPath && <img src={branding!.logoPath} alt={productName} />}
                                {/* Decorative: the first logo already names the installation. */}
                                {branding!.secondaryLogoPath && <img src={branding!.secondaryLogoPath} alt="" />}
                            </span>
                        )
                        // Falls back to the product name in text, rather than a broken image.
                        : <span className="auth-wordmark">{productName}</span>}

                    <p className="auth-pitch">Interviews, recorded and scored in one place.</p>
                    <p className="auth-pitch-note">
                        Schedule a candidate, send the link, and review the transcript, the scores and
                        the proctoring record when they are done.
                    </p>
                </div>

                {/* Copyright left, build credit right, the gap between them doing the separating. */}
                <div className="auth-aside-foot">
                    <span>{branding?.footerText || branding?.companyName || productName}</span>
                    <a
                        className="app-credit"
                        href={DEVELOPER_URL}
                        target="_blank"
                        rel="noreferrer"
                    >{DEVELOPER_CREDIT}</a>
                </div>
            </aside>

            <main className="auth-form-side">
                <div className="auth-body">
                    <h1 className="auth-title">Sign in</h1>
                    <p className="auth-lede">Use the account your administrator set up for you.</p>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        {error && <ErrorNote message={error.message} />}

                        <Field label="Username" error={error?.fieldError('userName')}>
                            <TextInput
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                                autoComplete="username"
                                autoFocus
                                invalid={!!error?.fieldError('userName')}
                            />
                        </Field>

                        <Field label="Password" error={error?.fieldError('password')}>
                            <div className="auth-password">
                                <TextInput
                                    type={revealed ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    invalid={!!error?.fieldError('password')}
                                />
                                {/* One-time passwords are long and generated, and typing one blind is
                                    where most first sign-ins fail. */}
                                <button
                                    type="button"
                                    className="auth-reveal"
                                    aria-label={revealed ? 'Hide password' : 'Show password'}
                                    aria-pressed={revealed}
                                    onClick={() => setRevealed((on) => !on)}
                                >
                                    <i className={`bi ${revealed ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true" />
                                </button>
                            </div>
                        </Field>

                        <Button type="submit" busy={busy} className="auth-submit">
                            Sign in
                        </Button>
                    </form>
                </div>
            </main>
        </div>
    );
}
